import type { ReactNode } from 'react';
import { figure } from '../reportFrame';
import { useHoverTip } from './HoverTip';
import styles from './BarChart.module.css';

/** The ghost chart's bars, long to short, so it reads as a chart with nothing in it. */
const GHOST_WIDTHS = [78, 56, 40, 24];

/** A colour from the theme's highlight set: each reads on both themes. */
export type BarTone = 'blue' | 'green' | 'amber' | 'violet' | 'cyan' | 'magenta' | 'red' | 'grey';
export type BarSeries = { key: string; label: string; tone: BarTone };
export type BarRow = {
  key: string;
  /** The row's name, as text: it is what the table twin and a screen reader say. */
  label: string;
  values: Record<string, number>;
  /** What is behind the label (an area's pages): in a tooltip on the label, and in full in the table twin. */
  detail?: string;
};

/**
 * Horizontal bars, one row per item, each split into its series' parts
 * (TOADO-862). Every row is drawn to one scale, the largest row's total, so
 * rows compare by length. Drawn in HTML from theme tokens, so it reads on
 * either theme and at any width; the bars are hidden from screen readers,
 * which read the table beside them with the same numbers. Under reduced
 * motion the bars are simply there. Pointing at a part says what it is and
 * its share of the row; pointing at a label shows its detail (16 Oct).
 * `hideCaption` keeps the caption for the table twin only, when a heading
 * beside the chart already names it.
 */
export function BarChart({ caption, series, rows, testId, empty, lowerBound = false, hideCaption = false }: {
  caption: string; series: BarSeries[]; rows: BarRow[]; testId?: string; empty?: ReactNode;
  /** Every count can only be an undercount (the report read short): each is shown with a "+". */
  lowerBound?: boolean;
  hideCaption?: boolean;
}) {
  const tip = useHoverTip();
  const totals = rows.map((row) => series.reduce((sum, part) => sum + (row.values[part.key] ?? 0), 0));
  const max = Math.max(1, ...totals);
  return (
    <figure className={styles.chart} data-testid={testId}>
      <figcaption className={hideCaption ? styles.srOnly : styles.caption}>{caption}</figcaption>
      {rows.length === 0 ? (
        // Not enough data yet (16 Oct): faint bars where the chart would be, its note over them.
        <div className={styles.ghost} data-testid="chart-ghost">
          <div className={styles.ghostRows} aria-hidden="true">
            {GHOST_WIDTHS.map((width, index) => <span key={index} className={styles.ghostRow}><span className={styles.ghostLabel} /><span className={styles.ghostBar} style={{ width: `${width}%` }} /></span>)}
          </div>
          <div className={styles.ghostNote}>{empty ?? null}</div>
        </div>
      ) : <>
        {series.length > 1 && (
          <ul className={styles.legend} aria-hidden="true">
            {series.map((part) => <li key={part.key}><span className={`${styles.swatch} ${styles[part.tone]}`} />{part.label}</li>)}
          </ul>
        )}
        <div className={styles.rows} aria-hidden="true">
          {rows.map((row, index) => (
            <div key={row.key} className={styles.row}>
              <span className={styles.label} data-detail={row.detail ? '' : undefined} {...(row.detail ? tip.bind(row.detail) : {})}>
                {row.label}
              </span>
              <span className={styles.track}>
                {series.map((part) => {
                  const value = row.values[part.key] ?? 0;
                  if (value <= 0) return null;
                  const share = Math.round((value / Math.max(1, totals[index]!)) * 100);
                  const said = series.length > 1
                    ? `${row.label}\n${part.label}: ${figure(value, lowerBound)} of ${figure(totals[index]!, lowerBound)} (${share}%)`
                    : `${row.label}\n${part.label}: ${figure(value, lowerBound)}`;
                  return <span key={part.key} className={`${styles.part} ${styles[part.tone]}`} style={{ width: `${(value / max) * 100}%` }} data-part={part.key} {...tip.bind(said)} />;
                })}
              </span>
              <span className={styles.total}>{figure(totals[index]!, lowerBound)}</span>
            </div>
          ))}
        </div>
        <table className={styles.srOnly}>
          <caption>{caption}</caption>
          <thead><tr><th scope="col">Name</th>{series.map((part) => <th key={part.key} scope="col">{part.label}</th>)}{series.length > 1 && <th scope="col">Total</th>}</tr></thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.key}>
                <th scope="row">{row.label}{row.detail ? `: ${row.detail}` : ''}</th>
                {series.map((part) => <td key={part.key}>{figure(row.values[part.key] ?? 0, lowerBound)}</td>)}
                {series.length > 1 && <td>{figure(totals[index]!, lowerBound)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </>}
      {tip.node}
    </figure>
  );
}
